import { TextAnchorCell, TextCell } from "@similarweb/pro-logic-components";
import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";

import { TrafficShareWithVisits } from "app/components/React/Table/cells/TrafficShareWithVisits";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, spendFilter } from "app/filters/ngFilters";
import type { SwitcherOption } from "app/pages/digital-marketing/ad-intelligence/common/shared/ShareTypeSwitcher";
import { NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import type { MetricShareKey } from "./topIndustriesMetric";
import {
    formatIndustryForAPI,
    getIndustryLabel,
    getMetricKey,
    getTopIndustriesIndexColumn,
    VALUE_COLUMN_TITLE_KEYS,
} from "./topIndustriesMetric";

export interface ITopIndustryRow {
    category: string;
    subCategory: string;
    spend: number;
    spendShare: number;
    impressions: number;
    impressionsShare: number;
    visits: number;
    visitsShare: number;
}

export const getTopIndustriesColumns = (
    selectedSwitcherOption: SwitcherOption,
    currentPage: number,
): ColumnsType<ITopIndustryRow> => {
    const industryColumn = {
        title: () => translate("ad.intelligence.publisher.overview.top.industries.column.title"),
        dataIndex: "category",
        render: (_category: string, row: ITopIndustryRow) => (
            <TextAnchorCell
                href={displayAdvertisingNavigation.overview.getLink({
                    industry: formatIndustryForAPI(row),
                })}
            >
                {getIndustryLabel(row)}
            </TextAnchorCell>
        ),
    } satisfies ColumnType<ITopIndustryRow>;

    const dataKey = getMetricKey(selectedSwitcherOption);
    const shareKey: MetricShareKey = `${dataKey}Share`;

    const valueColumn = {
        title: translate(VALUE_COLUMN_TITLE_KEYS[dataKey]),
        align: "left" as const,
        dataIndex: dataKey,
        render: (value: number, row: ITopIndustryRow) => {
            if (!value) {
                return <TextCell>{NA_VALUE}</TextCell>;
            }

            return (
                <TrafficShareWithVisits
                    row={{ TotalVisits: value }}
                    value={row[shareKey]}
                    totalVisitsFilter={
                        dataKey === "spend" ? () => (v: number) => spendFilter(v, abbrNumberFilter(), 100) : undefined
                    }
                    applyAbbrNumberFilter={dataKey !== "spend"}
                    undefinedSign={NA_VALUE}
                />
            );
        },
    } satisfies ColumnType<ITopIndustryRow>;

    return [getTopIndustriesIndexColumn(currentPage), industryColumn, valueColumn];
};
